import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Checkbox, CircularProgress, useMediaQuery, useTheme } from '@material-ui/core';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { MTable } from '../../../../../Components';
import { PlanningView } from '../PlanningLK/View/PlanningView';
import { StepData } from './types';
import { StepButtonBar } from './StepButtonBar';
import EvaluationStepInfo from './EvaluationStepInfo';

const PRIMARY = '#c4122f';

interface Step0SelectPlanProps {
  onBack: () => void;
  onCancel: () => void;
  isFirstStep: boolean;
  currentData: StepData;
  currentStepName: any;
  evalutionStepInfo: any;
  onUpdateEvalution?: (data: any) => void;
  customerId: number | null;
  api: any;
  requirementTypeOptions: { id: number; name: string }[];
  companyUnits: { id: number; name: string }[];
  onPlanSelected: (data: Partial<StepData>, compUnit: any, reqType: any, lawIds: number[], planId: string) => void;
}

const Step0SelectPlan = ({
  onBack, onCancel, isFirstStep, currentData,
  customerId, api, requirementTypeOptions, companyUnits,
  onPlanSelected, evalutionStepInfo, currentStepName, onUpdateEvalution,
}: Step0SelectPlanProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedPlanIdRef = useRef<string | null>(currentData._selectedPlanId ?? null);
  const [selectionVersion, setSelectionVersion] = useState(0);

  const setSelectedPlanId = useCallback((id: string | null) => {
    selectedPlanIdRef.current = id;
    setSelectionVersion(v => v + 1);
  }, []);

  const togglePlan = useCallback((planId: string) => {
    const next = selectedPlanIdRef.current === planId ? null : planId;
    setSelectedPlanId(next);
  }, [setSelectedPlanId]);

  useEffect(() => {
    let cancelled = false;
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await api.getAllcompliancePlan(customerId, { filters: {}, search: '' });
        if (!cancelled) {
          const allPlans = response?.customerPlans || [];
          setPlans(
            allPlans
              .filter((p: any) =>  p.isActive)
              .map((p: any) => ({ ...p, _planId: String(p.id) })),
          );
        }
      } catch (e) {
        console.error('Error fetching plans:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPlans();
    return () => { cancelled = true; };
  }, [api, customerId]);

  const columns = useMemo(() => [
    {
      title: '',
      field: '__select',
      width: '48px',
      sorting: false,
      removable: false,
      hiddenByColumnsButton: true,
      render: (row: any) => (
        <Checkbox
          checked={selectedPlanIdRef.current === String(row._planId)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => togglePlan(String(row._planId))}
          style={{ color: PRIMARY }}
        />
      ),
    },
    { title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.name-of-control' }), field: 'name' },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Company-unit' }),
      field: 'customerCompanyUnitId',
      render: (row: any) => row?.customerCompanyUnitId?.name,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Start-date' }),
      field: 'startDate',
      render: (row: any) => moment(row?.startDate).format('YYYY-MM-DD'),
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.End-date' }),
      field: 'endDate',
      render: (row: any) => moment(row?.endDate).format('YYYY-MM-DD'),
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Recuring-interval' }),
      field: 'recurrentInterval',
      render: (row: any) => row?.recurrentInterval || '-',
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Number-of-controls' }),
      field: 'lawCount',
      render: (row: any) => row.lawCount,
      sorting: false,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [formatMessage, togglePlan, selectionVersion]);

  const handleNext = async () => {
    const currentId = selectedPlanIdRef.current;
    const plan = plans.find((p: any) => p._planId === currentId);
    if (!plan || isSubmitting) return;

    try {
      setIsSubmitting(true);
      let lawIds: number[] = [];
      try {
        const result = await api.getPlanAllLawID(plan._planId);
        lawIds = result?.legislationIds || [];
      } catch (e) {
        console.error('Error fetching plan laws:', e);
      }

      const compUnit = companyUnits.find(
        (u: any) => u.id === (plan.customerCompanyUnitId?.id ?? plan.customerCompanyUnitId),
      );
      const reqType = requirementTypeOptions.find((t: any) => t.id === plan.type);
      
      // Detect if this is a one-time plan (no recurrence interval)
      const isOneTimePlan = !plan?.recurrentInterval;

      const data: Partial<StepData> = {
        CustomerCompanyUnitId: compUnit?.id,
        _companyUnitName: compUnit?.name || plan.customerCompanyUnitId?.name,
        StartDate: plan.startDate ? moment(plan.startDate).format('YYYY-MM-DD') : undefined,
        EndDate: plan.endDate ? moment(plan.endDate).format('YYYY-MM-DD') : undefined,
        type: plan.type,
        _typeName: reqType?.name,
        RecurrentInterval: plan.recurrentInterval || undefined,
        Reminder: plan.reminder ?? undefined,
        _sendReminder: plan.reminder != null,
        _resolvedPlanType: isOneTimePlan ? 'one-time' : 'recurring',
      };

      onPlanSelected(data, compUnit || null, reqType || null, lawIds, plan._planId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentSelectedId = selectedPlanIdRef.current;
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(3);

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
          }}
        >
          {loading ? (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(6) }}>
              <CircularProgress />
            </Box>
          ) : (
           <Box style={{ maxHeight: '500px', overflow: 'auto' }}>
              <MTable
                key={`plan-table-${selectionVersion}`}
                columns={columns}
                data={plans}
                options={{ detailPanelColumnAlignment: 'right', pageSizeOptions: [10, 25, 50, 100] }}
                onRowClick={(_: any, row: any) => {
                  if (row) togglePlan(String(row._planId));
                }}
                detailPanel={[
                  {
                    tooltip: formatMessage({ id: 'text.expend' }),
                    render: (rowData: any) => <PlanningView planLawsData={rowData?.rowData} />,
                  },
                ]}
              />
            </Box>
          )}
        </div>

        <EvaluationStepInfo evalutionStepInfo={evalutionStepInfo} currentStepName={currentStepName} onUpdate={onUpdateEvalution} />
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleNext}
          nextLabel="form.save.and.back"
          nextDisabled={!currentSelectedId || isSubmitting}
          isLoading={isSubmitting}
          showBack={!isFirstStep}
          formatMessage={formatMessage}
        />
      </Box>
    </div>
  );
};

export default Step0SelectPlan;